import React, { useEffect, useState } from 'react';

import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import { RunDetails } from '../../types';
import { parseUtcDate } from '../../utils';
import Spinner from '../../components/Spinner';

interface SyncChartProps {
  syncData: RunDetails[];
  loading?: boolean;
}

const SyncChart: React.FC<SyncChartProps> = ({ syncData, loading = false }) => {
  const [dailyData, setDailyData] = useState<{ date: string; count: number }[]>([]);
  const [hasData, setHasData] = useState<boolean>(false);

  useEffect(() => {
    if (syncData.length === 0){
      setHasData(false);
      return;
    }

    // Filter successful runs
    const completed = syncData.filter((r) => r.result === "SUCCESS");

    const today = dayjs().startOf("day");
    const daysWindow: { date: string; count: number }[] = [];
    const dayIndex: Record<string, number> = {};

    // Build last 10 days bucket
    for (let i = 9; i >= 0; i--) {
      const day = today.subtract(i, "day");
      const key = day.format("YYYY-MM-DD");
      dayIndex[key] = daysWindow.length;
      daysWindow.push({ date: key, count: 0 });
    }

    // Count runs per day
    completed.forEach((rec) => {
      const key = dayjs(parseUtcDate(rec.runDatetime)).startOf("day").format("YYYY-MM-DD");
      if (key in dayIndex) {
        daysWindow[dayIndex[key]].count += 1;
      }
    });

    setDailyData(daysWindow);
    setHasData(true);
  }, [syncData]);


  return (
    <div className="flex flex-col flex-1 px-4 py-5 bg-white rounded-2xl min-h-[240px] min-w-[240px] shadow-md">
      <div className="text-base font-semibold text-gray-900">Geslaagde synchronisaties laatste 10 dagen</div>

      <div className="w-full h-64 mt-3">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Spinner />
          </div>
        ) : !hasData ? (
          <div className="flex items-center justify-center h-full text-gray-500">Nog geen data</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => dayjs(date).format("DD MMM")}
              />
              <YAxis allowDecimals={false} />
              <Tooltip
                labelFormatter={(label) => dayjs(label).format("DD MMM YYYY")}
                formatter={(value) => [value as number, "runs"]}
              />
              <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SyncChart;
